import React, { CSSProperties, useMemo } from 'react';
import Slider, { Settings } from 'react-slick';
import { useTypedSelector } from '../../../../store/reducers/use-typed-selector';
import { PageModule, Session, SessionTypesEnum } from '../../../../types/working-model';
import EditorMainEventBanner from '../../../general-ui/main-event-banner/main-event-banner';
import Icon, { COLORS, ICONS } from '../../../general-ui/icon';
import MainEventBannerBreakout from '../main-event-banner-breakout/main-event-banner-breakout';
import MainEventBanner from '../main-event-banner/main-event-banner';
import { MainEventBannerLiveSession } from './main-event-banner-live';
import { useParams } from 'react-router';
import { ParamsProps } from '../../live-event';
import '../../../../scss/live-event/base/modules/main-event-banner-live-carousel.scss';
import { getTemplateClassName } from '../../../../utils/utils';


interface IArrowProps {
	className?: string;
	style?: CSSProperties;
	onClick?: () => void;
}

export function PrevArrow(props: IArrowProps): JSX.Element {
	const { className, style, onClick } = props;

	return (
		<div onClick={onClick} className={className} style={{ ...style }}>
			<Icon name={ICONS.KEYBOARD_ARROW_LEFT} size={20} color={COLORS.WHITE} />
		</div>
	);
}

export function NextArrow(props: IArrowProps): JSX.Element {
	const { className, style, onClick } = props;

	return (
		<div onClick={onClick} className={className} style={{ ...style }}>
			<Icon name={ICONS.KEYBOARD_ARROW_RIGHT} size={20} color={COLORS.WHITE} />
		</div>
	);
}


interface IMainEventBannerLiveCarouselProps {
	module: PageModule;
	template: string;
	liveSessionList: Session[];
	isAgendaPageEditor?: boolean;
}

const MainEventBannerLiveCarousel: React.FC<IMainEventBannerLiveCarouselProps> = (props: IMainEventBannerLiveCarouselProps) => {
	const { module, liveSessionList, template, isAgendaPageEditor = false } = props;

	const eventBundle = useTypedSelector(state => state.LiveEventReducer.eventBundle);
	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const event = eventBundle ?? workingEvent;

	const { language } = useParams<ParamsProps>();

	const sliderSettings: Settings = {
		prevArrow: <PrevArrow />,
		nextArrow: <NextArrow />,
		pauseOnHover: true,
		autoplay: true,
		autoplaySpeed: 5000,
		// if banner module is off, start carousel on first slide, otherwise start on second slide and use banner module as first slide.
		initialSlide: module.is_on ? 1 : 0,
		dots: true,
		appendDots: dots => {
			return (
				<div>
					<ul>
						{dots}
					</ul>
				</div>
			);
		},
	};

	const RenderedLiveSessions = useMemo(() => {
		return liveSessionList.map(session => {

			// breakout session has its own live banner
			if (session.session_type === SessionTypesEnum.breakoutRooms) {
				const _template = workingEvent
					? getTemplateClassName(workingEvent.template.name)
					: template;

				return (
					<div key={module.id} className="session-banner-container">
						<div style={{ maxHeight: '460px' }} key={module.id}>
							<MainEventBannerBreakout session={session} template={_template} hideMobileHeader useSessionThumbnail hideCountdown={!!workingEvent} />
						</div>
					</div>
				);
			}

			return (
				<div key={module.id} className="session-banner-container">
					<MainEventBannerLiveSession
						module={module}
						template={template}
						liveSession={session}
						useSessionImage
					/>
				</div>
			);
		});
	}, [liveSessionList, module, template, workingEvent]);

	return (
		<div className="main-event-banner-live-carousel">
			{/* display editable version in editor, but on agenda page use live-event version so its not editable */}
			{module.is_on ? (
				<Slider {...sliderSettings}>
					{(workingEvent && !isAgendaPageEditor) ? (
						<EditorMainEventBanner
							key={module.id}
							page_module={module}
							template={getTemplateClassName(workingEvent.template.name)}
							language={language}
							baseLanguage={'en'}
						/>
					) : (
						<MainEventBanner
							module={module}
							template={template}
							key={module.id}
							eventBundle={event}
						/>
					)}
					{RenderedLiveSessions}
				</Slider>
			) : (
				<Slider {...sliderSettings}>
					{RenderedLiveSessions}
				</Slider>
			)}

		</div>
	);
};

export default MainEventBannerLiveCarousel;
